import Configuration from '@/agilis/util/Configuration'
import DataTableV2Title from '@/agilis/components/DataTableV2/DataTableV2Title/DataTableV2Title.vue'
import MarkdownViewer from '@/agilis/components/Form/FormMarkdown/viewer/MarkdownViewer.vue'
import EntityDetails from '@/agilis/components/EntityDetails/EntityDetails.vue'
import TrackerDataTableItemAssociations from '@/applications/scrum/components/TrackerDataTable/TrackerDataTableItemAssociations/TrackerDataTableItemAssociations.vue'

import toursMixin from '@/agilis/components/Tour/mixins/toursMixin'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'DataTableItemView',
  mixins: [toursMixin],

  props: {
    api: String,
    title: String,
    subtitle: String,
    itemDTO: Object,
    configuration: Object,
    entityConfiguration: Object
  },

  components: {
    DataTableV2Title,
    MarkdownViewer,
    EntityDetails,
    TrackerDataTableItemAssociations
  },

  data () {
    return {
      itemId: this.itemDTO.id,
      fetchedItemDTO: {}
    }
  },

  mounted: function () {
    this.fetchData(this.computedItemId)
  },
  computed: {
    ...mapGetters({
      userId: 'userDataStore/userId',
      selectedContext: 'contextStore/selectedContext',
      applicationId: 'contextStore/applicationId',
      workspaceId: 'contextStore/workspaceId',
      trackerId: 'contextStore/trackerId',
      loading: 'loaderStore/loading'
    }),

    routeName () {
      return this.$route.name ? this.$route.name : 'applicationWorkspace'
    },

    viewViaQuery () {
      return !!this.configuration.queryViewMode
    },

    datatable () {
      if (this.viewViaQuery) {
        const queryId = this.configuration.queryViewMode

        // Which datatable definition to use?
        // 1- datatable defined specifically for query in view mode: "queryViewModeDatatable"
        // 2- datatable defined generically for query (i.e: share between select/edit/view): "queryDatatable"
        // 3- datatable defined inside the query: the datatable used on BE side
        const dataTableFE = this.configuration.queryViewModeDatatable || this.configuration.queryDatatable
        const { dataTable: dataTableBE } = Configuration.getApplications()[this.applicationId]?.query[queryId]

        return dataTableFE || dataTableBE
      }

      return this.configuration
    },

    trackerForRendering () {
      return Configuration.datatable.mapItemDTO(this.datatable, this.fetchedItemDTO)
    },

    computedTitle () {
      if (this.configuration?.itemTitle) {
        return this.$t(this.configuration.itemTitle, this.trackerForRendering)
      }

      return this.title + ' ' + this.computedItemId
    },

    computedSubTitle () {
      if (this.configuration?.itemSubtitle) {
        return this.$t(this.configuration.itemSubtitle, this.trackerForRendering)
      }

      return this.subtitle
    },

    computedItemId () {
      // usiamo il trackerId perché lo recuperiamo dal contesto settato dal router
      // TODO definire nuove routes per entità non tracker
      // e.g. per /application/sales/workspace/sorintlab.presale/company/view/1
      return this.trackerId
    },

    formFields () {
      return this.datatable.columns
    },

    maxFormColumns () {
      const formRows = this.datatable.formRows
      return formRows.reduce(function (result, element) {
        return result >= element.cols.length * 2 ? result : element.cols.length * 2
      }, 0)
    },

    selectedContextWithTrackerId () {
      return Object.assign({ trackerId: this.trackerId }, this.selectedContext)
    },

    entityTypes () {
      return this.entityConfiguration.entityTypes || {}
    },

    jlAssertionContext () {
      return {
        tracker: this.fetchedItemDTO
      }
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'startLoader': 'loaderStore/startLoader',
      'stopLoader': 'loaderStore/stopAllLoaders'
    }),

    closeItem (itemDTO, event) {
      this.$emit('cancel')

      const routeCallback = this.$router.agilis.getRouteNextRoute()
      if (routeCallback && typeof routeCallback === 'function') {
        routeCallback()
      } else {
        this.$router.agilis.viewTable()
      }
    },

    /**
     * Fetch data calling the standard entity GET API
     */
    fetchData (itemId) {
      if (this.viewViaQuery) {
        return this.fetchDataViaQuery(itemId)
      }

      this.setLoader(true)

      const url = this.$axios.agilisUrlDataTableEdit(this.api, itemId)
      this.$axios.get(
        url,
        this.$axios.agilisRequestConfig(url)
      ).then((response) => {
        this.onFetchResponse(response.data)
      }).catch((error) => {
        this.notifyError(error)
      }).finally(() => {
        this.setLoader(false)
      })
    },

    /**
     * Fetch data calling the query API to retrieve the entity joined with other entities
     */
    fetchDataViaQuery (itemId) {
      this.setLoader(true)

      const url = this.$axios.agilisUrlQuery(this.applicationId, this.configuration.queryViewMode)
      const filterById = { field: 'id', operator: 'EQUALS', value: itemId }
      const body = Configuration.datatable.paginatedRequestBody(this.datatable, {}, '', [filterById], [])
      const config = this.$axios.agilisRequestConfig(url)

      this.$axios.post(url, body, config).then((response) => {
        this.onFetchResponse(response.data.items[0])
      }).catch((error) => {
        this.notifyError(error)
      }).finally(() => {
        this.setLoader(false)
      })
    },

    /**
     * Update model with fetch response from API
     */
    onFetchResponse (entity) {
      this.fetchedItemDTO = {
        trackerTypeId: this.selectedContext.trackerTypeId,
        ...JSON.parse(JSON.stringify(entity))
      }
    },

    /**
     * Loader utility function
     */
    setLoader (value) {
      const loader = { id: 'tracker-details' }
      if (value) this.startLoader(loader)
      else this.stopLoader(loader)
    },

    userCanAccessFeature (featureId) {
      return Configuration.auth.userCanAccessFeature(
        this.selectedContext, featureId, null, this.fetchedItemDTO, this.userId)
    },

    userCanAccessField (colId) {
      return Configuration.auth.userCanAccessField(this.selectedContext, colId)
    },

    userCanDo (operation) {
      return Configuration.auth.userCanDo(this.selectedContext, operation, this.fetchedItemDTO, this.userId)
    },

    editItem (item, event) {
      this.$router.agilis.edit({
        trackerId: item.id,
        routeName: this.routeName
      })
    }
  }

}
